import { Box, ButtonBase, Container, styled } from '@mui/material';
import clsx from 'clsx';
import { useState } from 'react';

import { routes } from '@/assets/constants/routes-no.ts';
import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';
import { NumberCircle } from '@/ui/NumberCircle.tsx';
import { NumberedList, NumberedListItem } from '@/ui/NumberedList.tsx';

import Umbrella from '../../../_common/umbrella.svg?react';
import Beach from '../_assets/beach.svg?react';
import Bed from '../_assets/bed.svg?react';
import Cloud from '../_assets/cloud.svg?react';
import Palm from '../_assets/palm.svg?react';
import Parasailer from '../_assets/parasailer.svg?react';
import styles from '../_assets/styles.module.css';
import SunReflection from '../_assets/sun-reflection.svg?react';
import Sun from '../_assets/sun.svg?react';
import { CTA } from '../_components/CTA.tsx';
import { useHasEnded } from '../_components/useHasEnded.ts';

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  width: '100%',
  isolation: 'isolate',

  '--height': '775px',
  [theme.breakpoints.up('sm')]: {
    '--height': '805px',
  },
  [theme.breakpoints.up('lg')]: {
    '--height': '950px',
  },
  // [theme.breakpoints.up('xl')]: {
  //   '--height': '1000px',
  // },

  height: 'var(--height)',
}));

export const TopSection = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  return (
    <>
      <Root>
        <Background />

        <Container maxWidth="xl" sx={{ pt: { xs: 4, md: 8 } }}>
          <Content activeCompetition={props.activeCompetition} winners={props.winners} />
        </Container>
      </Root>

      <Disclaimer />
    </>
  );
};

const Background = () => (
  <div className="absolute inset-0 isolate -z-10 flex flex-col overflow-hidden bg-linear-to-b from-[#D1ECFE] to-[#E8F6FF] to-50%">
    <Parasailer
      className={clsx(
        styles.parasailer,
        'absolute -right-20 bottom-56 scale-75 sm:bottom-64 xl:right-1/3 xl:bottom-88 xl:scale-100 2xl:bottom-92 2xl:left-1/2 2xl:scale-125',
      )}
    />
    <Sun className="absolute right-20 bottom-76 -z-10 hidden scale-50 sm:block md:bottom-80 md:scale-75 xl:right-3/12 xl:bottom-92 xl:scale-100 2xl:left-1/2 2xl:translate-x-72" />
    <SunReflection className="absolute right-20 bottom-72 hidden scale-50 sm:block md:bottom-68 md:scale-75 xl:right-3/12 xl:bottom-72 xl:scale-100 2xl:left-1/2 2xl:translate-x-72" />
    <Beach
      className={clsx(
        styles.beach,
        'mt-auto -mb-8 w-[500%] sm:w-[300%] lg:w-[150%] xl:mb-0 xl:w-full',
      )}
      preserveAspectRatio="none"
    />
    <Cloud className="absolute top-24 -left-40 xl:left-1/3" />
    <Cloud className="absolute top-48 right-4 scale-75 xl:top-6 xl:right-1/12 xl:scale-100" />
    <Cloud className="absolute right-28 bottom-92 hidden scale-33 sm:block md:right-36 md:bottom-96 xl:right-[30%] xl:bottom-104 xl:scale-50 2xl:left-1/2 2xl:translate-x-48" />
    <Umbrella className="absolute hidden xl:-right-12 xl:bottom-12 xl:block 2xl:right-0 2xl:left-1/2 2xl:translate-x-48" />
    <Bed className="absolute -bottom-14 -left-64 scale-75 sm:-left-42 xl:bottom-0 xl:left-0 xl:scale-100 min-[120rem]:left-1/2 min-[120rem]:-translate-x-210" />
    <Palm className="absolute -right-48 bottom-40 hidden xl:block 2xl:left-1/2 2xl:translate-x-108" />
  </div>
);

const Disclaimer = () => (
  <div className="bg-white pt-4">
    <Container maxWidth="md">
      <div className="text-center typography-v1-body4 text-balance">
        <LoanExampleText />
      </div>
    </Container>
  </div>
);

const Content = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  const ended = useHasEnded();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box
      sx={{
        maxWidth: { xs: 370, sm: 600, md: 750, xl: 800 },
        mx: { xs: 'auto', sm: 0 },
      }}
    >
      <h1 className="mb-4 text-2xl font-bold text-balance sm:text-5xl xl:text-6xl 2xl:text-7xl 2xl:leading-20">
        Er det du som våkner <span className="font-extrabold">gjeldfri</span> på ferie?
      </h1>
      <p className="mb-6 typography-v1-h4 md:mb-8 lg:mb-4">
        Nå kan du vinne en ferie til en verdi av 50 000 kr – og få slettet inntil 500 000 kr av din
        rentebærende forbruks og kredittkortgjeld.
      </p>
      <p className="mb-2 typography-v1-h4">For å delta:</p>

      <NumberedList
        sx={{ mb: 2 }}
        renderNumber={(props) => (
          <NumberCircle
            sx={{
              fontSize: 14,
              height: 20,
              backgroundColor: '#FBFDFF',
              color: '#031839',
            }}
            {...props}
          />
        )}
        renderLabel={(props) => <p {...props} className="typography-v1-body3" />}
      >
        <NumberedListItem>Logg inn med BankID</NumberedListItem>
        <NumberedListItem>Sjekk om du kan få lavere rente</NumberedListItem>
        <NumberedListItem>Delta i trekningen</NumberedListItem>
      </NumberedList>

      {ended && (
        <p className="mb-6 typography-v1-h4">
          <span>Denne trekningen er avsluttet. </span>
          <ButtonBase
            onClick={() => setIsOpen(true)}
            sx={{
              color: '#74C6FD',
              textDecoration: 'underline',
              mb: 0.5,
              fontWeight: 700,
              ml: 1,
            }}
          >
            Se vinneren her
          </ButtonBase>
        </p>
      )}

      <Box sx={{ mb: 2 }}>
        <a
          href={routes.MAY_2025_COMPETITION_TERMS}
          style={{ color: 'inherit' }}
          className="underline"
          target="_blank"
        >
          <span className="typography-v1-body3">Vilkår og betingelser for deltagelse</span>
        </a>
      </Box>

      <CTA
        activeCompetition={props.activeCompetition}
        onClick={() => {
          sendUserPortalEvent('may25_competition_cta_top_clicked');
        }}
      />

      {props.winners && (
        <CompetitionWinnersDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          winners={props.winners}
        />
      )}
    </Box>
  );
};
